import { useRef, useCallback } from "react";
import { useState } from "react";
import { useEffect } from "react";
import Document from "./Document";
import { Document as DocumentType } from "../types";
import Title from "./ui/Title";
import Button from "./ui/Button";
import { IconLoader2 } from "@tabler/icons-react";
import BACKEND_URL from "../config";

export interface DocFeedbackSummary {
    count: number;
    latestRating: number;
}

const Documents = ({ documents, getDocumentsFromCharacteristic, loading, isFullscreen }: { documents: DocumentType[], getDocumentsFromCharacteristic: () => void, loading: boolean, isFullscreen?: boolean }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [feedbackMap, setFeedbackMap] = useState<Record<string, DocFeedbackSummary>>({});

    useEffect(() => {
        const ids = documents.map(d => d.documentation_retrieved_id).filter(Boolean);
        if (ids.length === 0) return;
        fetch(`${BACKEND_URL}/vnog/my_documentation_feedback/?doc_ids=${ids.join(',')}`, {
            credentials: 'include',
        })
            .then(res => res.ok ? res.json() : {})
            .then((data: Record<string, Array<{ rating: number }>>) => {
                const map: Record<string, DocFeedbackSummary> = {};
                for (const [docId, entries] of Object.entries(data)) {
                    if (entries.length > 0) {
                        map[docId] = { count: entries.length, latestRating: entries[0].rating };
                    }
                }
                setFeedbackMap(map);
            })
            .catch(() => {});
    }, [documents]);

    const handleFeedbackSubmitted = useCallback((docId: number, rating: number) => {
        setFeedbackMap(prev => {
            const existing = prev[String(docId)];
            return {
                ...prev,
                [String(docId)]: {
                    count: (existing?.count ?? 0) + 1,
                    latestRating: rating,
                },
            };
        });
    }, []);
    
    const scrollDocuments = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            // Scroll by the width of 2 documents (2 * 20vw)
            const scrollDistance = 448; // ~2 compact cards (w-56)
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollDistance : scrollDistance,
                behavior: 'smooth'
            });
        }
    };

    // Check if scrolling is possible
    const checkScrollability = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            // Can scroll left if not at the beginning
            setCanScrollLeft(scrollLeft > 0);
            // Can scroll right if not at the end
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5); // 5px buffer for rounding
        }
    };

    // Check scrollability on mount and when documents change
    useEffect(() => {
        checkScrollability();
        // Set initial scroll state after documents render
        setTimeout(checkScrollability, 100);
    }, [documents]);

    // Add scroll event listener
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);
            
            return () => {
                scrollContainer.removeEventListener('scroll', checkScrollability);
                window.removeEventListener('resize', checkScrollability);
            };
        }
    }, []);

    return (
        <div className={`flex flex-col gap-2 items-center w-full ${isFullscreen ? 'flex-1 min-h-0' : 'flex-shrink-0'}`}>
            <div className="flex flex-row justify-between gap-4 w-full flex-shrink-0">
                <div className="flex flex-row gap-2 items-center justify-between w-full">
                    <div className="flex flex-row gap-2 items-center">
                        <Title title="Documenten" subtitle="Kennisbank van de VNOG" />
                        {loading && <IconLoader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    <Button onClick={getDocumentsFromCharacteristic}>
                        Update
                    </Button>
                </div>
            </div>


            <div className={`flex flex-row items-center w-full ${isFullscreen ? 'flex-1 min-h-0' : ''} relative`}>
                {documents.length > 0 && canScrollLeft && (
                    <button 
                        onClick={() => scrollDocuments('left')} 
                        className="absolute left-0 z-10 bg-white/80 hover:bg-white shadow-md rounded-full p-2 transform -translate-x-1/2"
                        aria-label="Scroll left"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M15 6l-6 6l6 6" />
                        </svg>
                    </button>
                )}
                
                <div
                    ref={scrollContainerRef}
                    data-docs-scroll
                    className="flex flex-row gap-4 w-full overflow-x-scroll scrollbar-hide items-start min-h-[252px]"
                >
                    {documents.map(document => (
                        <Document 
                            key={document.document.id} 
                            document={document.document} 
                            similarity_score={document.similarity_score} 
                            metadata={document.metadata} 
                            document_name={document.document_name}
                            documentation_retrieved_id={document.documentation_retrieved_id}
                            feedback={feedbackMap[String(document.documentation_retrieved_id)]}
                            onFeedbackSubmitted={handleFeedbackSubmitted}
                        />
                    ))}
                </div>
                
                {documents.length > 0 && canScrollRight && (
                    <button 
                        onClick={() => scrollDocuments('right')} 
                        className="absolute right-0 z-10 bg-white/80 hover:bg-white shadow-md rounded-full p-2 transform translate-x-1/2"
                        aria-label="Scroll right"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M9 6l6 6l-6 6" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default Documents;